import { getAdminIdToken } from "./firebase";

export type AdminRole = "support" | "ops" | "finance" | "superadmin";
export type AdminPermission =
  | "orders:read"
  | "reports:download"
  | "reports:retry"
  | "payments:refund"
  | "admins:manage";
export type OrderStatus =
  | "created"
  | "payment_requested"
  | "confirming"
  | "paid"
  | "payment_failed"
  | "cancelled"
  | "refunded";
export type ReportStatus = "queued" | "generating" | "ready" | "failed";

export interface AdminSession {
  uid: string;
  email: string;
  displayName: string;
  role: AdminRole;
  roleVersion: number;
  permissions: AdminPermission[];
  authTime: number | null;
}

export interface ReportView {
  id: string;
  orderId: string;
  ownerId: string;
  status: ReportStatus;
  stage: string;
  progress: number;
  taskEnqueued: boolean;
  attemptCount: number;
  error: { code: string; failedStage: string; retryable: boolean; attemptCount: number } | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  artifacts: { pdfBytes: number; pageCount: number; expiresAt: string } | null;
}

export interface OrderRecordView {
  id: string;
  ownerId: string;
  chartId: string;
  productCode: string;
  productName: string;
  reportYear: number;
  amount: number;
  currency: "TWD";
  paymentProvider: "ecpay";
  paymentTradeNo: string;
  paymentProviderTransactionId: string | null;
  status: OrderStatus;
  reportId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrderListItem {
  orderId: string;
  ownerId: string;
  productName: string;
  amount: number;
  currency: "TWD";
  status: OrderStatus;
  paymentTradeNo: string;
  report: null | {
    reportId: string;
    status: ReportStatus;
    stage: string;
    progress: number;
    attemptCount: number;
    taskEnqueued: boolean;
    failure: ReportView["error"];
  };
  createdAt: string;
  updatedAt: string;
}

export interface OperatorActionView {
  id: string;
  action: "report.retry";
  actorEmail: string;
  actorRole: AdminRole;
  reason: string;
  status: "accepted" | "enqueued" | "delivery_pending";
  before: { status: ReportStatus; stage: string; progress: number; updatedAt: string };
  after: { status: ReportStatus; stage: string; progress: number; updatedAt: string };
  createdAt: string;
  completedAt: string | null;
}

export interface OrderDetailResponse {
  order: OrderRecordView;
  report: ReportView | null;
  downloads: { htmlUrl: string; pdfUrl: string; expiresAt: string } | null;
  actions: OperatorActionView[];
}

const baseUrl = (
  import.meta.env.VITE_REPORT_API_BASE_URL ||
  "https://ziwei-report-api-1078181891220.asia-east1.run.app"
).replace(/\/$/, "");

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAdminIdToken();
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...init.headers
    }
  });
  const body = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
  if (!response.ok) {
    const error = new Error(body?.error?.message || "後台目前無法完成操作。") as Error & { code?: string };
    error.code = body?.error?.code;
    throw error;
  }
  return body as T;
}

export const adminApi = {
  getSession: () => request<{ admin: AdminSession }>("/v1/admin/session"),
  listOrders: (input: { query?: string; status?: string; cursor?: string }) => {
    const query = new URLSearchParams({ limit: "30" });
    if (input.query) query.set("query", input.query);
    if (input.status) query.set("status", input.status);
    if (input.cursor) query.set("cursor", input.cursor);
    return request<{ orders: OrderListItem[]; nextCursor: string | null }>(
      `/v1/admin/orders?${query}`
    );
  },
  getOrder: (orderId: string) =>
    request<OrderDetailResponse>(`/v1/admin/orders/${encodeURIComponent(orderId)}`),
  retryReport: (reportId: string, reason: string) =>
    request<{ actionId: string; reportId: string; deliveryPending: boolean }>(
      `/v1/admin/reports/${encodeURIComponent(reportId)}/retry`,
      {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason })
      }
    )
};
